/**
 * Realtime listeners for the project board.
 *
 * Events ride the `company:{companyId}` room. We join it ourselves (idempotent
 * `sockets/subscribe`) so the board works even on a deep-link where the
 * presence panel never mounted. Each handler:
 *   - ignores events for a different company or a board we're not viewing;
 *   - otherwise applies the delta through the normalized slice reducers.
 *
 * The local optimistic update + idempotent/dedupe-safe reducers mean a tab
 * re-applying its own echoed event is a no-op, so no echo suppression is needed.
 *
 * A reconnect gets a fresh socket id (server room membership is lost), so we
 * re-subscribe and refetch the board to catch anything missed while offline.
 */
import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector } from "hooks";
import { AppDispatch } from "configureStore";
import { socket, socketGet } from "services/socket-client";
import {
  applyCardRemoved,
  applyRealtimeCardUpserted,
  applyRealtimeCardMoved,
  applyRealtimeColumnUpserted,
  applyRealtimeColumnMoved,
  fetchProjectBoardWithCurrentFilters,
  setCardSeen,
} from "containers/project-board/project-board-container.slice";
import { ProjectCardData, OrderRef } from "containers/project-board/types";

interface BasePayload {
  companyId?: string;
  boardId?: string;
  fromBoardId?: string;
}

interface MovePayload extends BasePayload {
  id: string;
  projectBoardColumn: string;
  order?: number | null;
  rebalanced?: boolean;
  columnOrders?: OrderRef[];
}

interface ColumnMovePayload extends BasePayload {
  id: string;
  order?: number | null;
  rebalanced?: boolean;
  columnOrders?: OrderRef[];
}

export function useProjectBoardSocket(
  companyId: string | undefined,
  boardId: string | undefined
) {
  const dispatch: AppDispatch = useDispatch();
  // Current user id, read in the (stable) socket effect via a ref so a login
  // change doesn't tear down and re-subscribe the board listeners.
  const currentUserId = useAppSelector((s) => s.authReducer?.role?.user);
  const currentUserIdRef = useRef(currentUserId);
  currentUserIdRef.current = currentUserId;

  useEffect(() => {
    if (!companyId || !boardId) return undefined;
    let cancelled = false;

    // Best-effort join of company:{id}. Safe to call repeatedly.
    const subscribe = () => {
      socketGet(`/${companyId}/sockets/subscribe`).catch(() => {
        /* presence panel / polling recover state; nothing to do here */
      });
    };

    // Relevant if it concerns this company and either the target OR the source
    // board is the one we're viewing (so a card moved OFF this board is dropped
    // here, not left lingering until reload).
    const relevant = (p: BasePayload | undefined): p is BasePayload =>
      !!p &&
      String(p.companyId) === String(companyId) &&
      (String(p.boardId) === String(boardId) ||
        String(p.fromBoardId) === String(boardId));

    const onCardUpserted = (...args: unknown[]) => {
      const p = args[0] as
        | (BasePayload & { card?: ProjectCardData })
        | undefined;
      if (!relevant(p) || !p.card) return;
      dispatch(applyRealtimeCardUpserted({ companyId, boardId, card: p.card }));
    };
    const onCardDeleted = (...args: unknown[]) => {
      const p = args[0] as (BasePayload & { id?: string }) | undefined;
      if (!relevant(p) || !p.id) return;
      dispatch(applyCardRemoved({ id: p.id }));
    };
    const onCardMoved = (...args: unknown[]) => {
      const p = args[0] as MovePayload | undefined;
      if (!relevant(p)) return;
      dispatch(applyRealtimeCardMoved({ companyId, boardId, payload: p }));
    };
    const onColumnUpserted = (...args: unknown[]) => {
      const p = args[0] as (BasePayload & { column?: unknown }) | undefined;
      if (!relevant(p) || !p.column) return;
      dispatch(
        applyRealtimeColumnUpserted({ companyId, boardId, column: p.column })
      );
    };
    const onColumnMoved = (...args: unknown[]) => {
      const p = args[0] as ColumnMovePayload | undefined;
      if (!relevant(p)) return;
      dispatch(applyRealtimeColumnMoved({ companyId, boardId, payload: p }));
    };
    // Personal "seen" sync: only the current user's own event flips their eye.
    const onCardSeen = (...args: unknown[]) => {
      const p = args[0] as
        | (BasePayload & { cardId?: string; userId?: string })
        | undefined;
      if (!relevant(p) || !p.cardId) return;
      if (String(p.userId) !== String(currentUserIdRef.current)) return;
      dispatch(setCardSeen({ id: p.cardId, seen: true }));
    };
    const onReconnect = () => {
      if (cancelled) return;
      subscribe();
      dispatch(fetchProjectBoardWithCurrentFilters(companyId, boardId));
    };

    socket.on("project-created", onCardUpserted);
    socket.on("project-updated", onCardUpserted);
    socket.on("project-deleted", onCardDeleted);
    socket.on("project-moved", onCardMoved);
    socket.on("project-seen", onCardSeen);
    socket.on("project-column-created", onColumnUpserted);
    socket.on("project-column-updated", onColumnUpserted);
    socket.on("project-column-moved", onColumnMoved);
    socket.on("reconnect", onReconnect);
    subscribe();

    return () => {
      cancelled = true;
      socket.off("project-created", onCardUpserted);
      socket.off("project-updated", onCardUpserted);
      socket.off("project-deleted", onCardDeleted);
      socket.off("project-moved", onCardMoved);
      socket.off("project-seen", onCardSeen);
      socket.off("project-column-created", onColumnUpserted);
      socket.off("project-column-updated", onColumnUpserted);
      socket.off("project-column-moved", onColumnMoved);
      socket.off("reconnect", onReconnect);
    };
  }, [companyId, boardId, dispatch]);
}
